import { useState, type FormEvent } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9p0c5iftmtnfcfh0f4g';

export default function AboutPage() {
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(e.currentTarget);
      const honeypot = formData.get('phone_alt') as string;
      if (honeypot && honeypot.trim() !== '') {
        setFormStatus('success');
        setFormName('');
        setFormEmail('');
        setFormMessage('');
        return;
      }

      const body = new URLSearchParams({
        name: formData.get('name') as string,
        email: formData.get('email') as string,
        message: formData.get('message') as string,
      }).toString();

      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const text = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(text); } catch { parsed = {}; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormName('');
        setFormEmail('');
        setFormMessage('');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
        setFormError(msg.includes('spam') ? '提交失败，请稍后再试' : msg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫 - 韩国漫画大全',
    description: '韩漫网站是专注于韩国漫画在线阅读的中文平台，提供免费下拉式漫画阅读体验，精选好看韩国漫画大全。',
    url: 'https://hanman.com/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫',
    url: 'https://hanman.com',
    description: '韩漫网站 - 免费阅读韩国漫画的在线平台',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9999',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
      email: 'contact@hanman.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫网站的漫画是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩漫网站上所有的韩国漫画均可免费阅读。我们致力于为广大漫画爱好者提供高质量的免费漫画阅读服务，支持下拉式流畅浏览，无需付费即可享受全部内容。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫网站的漫画多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫网站每日都会更新漫画内容，热门连载作品通常每周更新一到两话。我们与多家漫画平台保持合作，确保读者能够第一时间阅读到最新的韩漫章节。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在韩漫网站上找到好看的韩国漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页的热门推荐、人气排行了解当前最受欢迎的漫画，也可以使用分类筛选功能按照恋爱、奇幻、动作等类型浏览。每部漫画详情页还提供了相关推荐，帮助您发现更多喜欢的作品。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫网站支持手机阅读吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫网站采用响应式设计，完美适配手机、平板和电脑等多种设备。无论您使用什么设备，都能获得流畅的下拉式漫画阅读体验。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍与联系方式"
        description="韩漫网站是专注于韩国漫画在线阅读的中文平台，提供免费下拉式漫画阅读体验，精选好看韩国漫画大全。了解韩漫网站的品牌故事、用户口碑和联系方式。"
        keywords="韩漫网站, 关于我们, 品牌介绍, 联系方式, 韩国漫画, 免费阅读漫画, 漫画韩漫网址"
        canonicalPath="/about"
        schema={{ ...pageSchema, ...orgSchema, ...faqSchema }}
      />

      <div className="pt-16">
        <div className="w-full px-4 md:px-8 py-6 bg-background-50 border-b border-background-200/60">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 px-4 md:px-8 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                关于韩漫
              </h1>
              <p className="mt-4 text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                韩漫网站成立于2023年，是专注于<strong>韩国漫画在线阅读</strong>的中文平台。我们致力于为广大漫画爱好者提供最优质的<strong>免费漫画阅读</strong>服务，支持<strong>下拉式</strong>流畅浏览体验。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/60 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-book-open-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-2">内容精选</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  我们精心筛选<strong>好看韩国漫画大全</strong>，涵盖恋爱、奇幻、动作、悬疑等多元题材，确保每部作品都是高质量的阅读体验。
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/60 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-speed-up-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-2">阅读流畅</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  采用<strong>下拉式漫画阅读</strong>模式，无需翻页加载，一气呵成的阅读体验让您沉浸在漫画世界中。
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/60 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-4">
                  <i className="ri-refresh-line text-2xl text-secondary-500"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-2">每日更新</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  热门<strong>韩漫</strong>每日追踪更新，读者永远不会错过任何精彩章节，享受持续不断的阅读乐趣。
                </p>
              </div>
            </div>

            <div className="bg-background-100 rounded-2xl p-8 md:p-12 border border-background-200/60">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">品牌故事</h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  韩漫网站的创始团队是一群热爱<strong>韩国漫画</strong>的资深读者。在多年的漫画阅读经历中，我们发现国内缺少一个专注于韩漫、阅读体验流畅、内容质量高的中文平台。于是，我们决定自己打造一个这样的<strong>漫画韩漫网址</strong>。
                </p>
                <p>
                  从最初的小型分享站点，到如今拥有超过500万活跃读者的平台，韩漫网站始终秉持「为读者提供最佳阅读体验」的理念。我们与韩国多家漫画工作室建立了合作关系，确保读者能够第一时间阅读到最新、最热的<strong>韩国漫画</strong>作品。
                </p>
                <p>
                  我们的团队由漫画编辑、内容运营和技术开发人员组成，每天都在为提升<strong>韩漫网站</strong>的阅读体验而努力。无论是<strong>下拉式</strong>浏览的流畅度优化，还是漫画内容的精选推荐，每一个细节都凝聚着我们对漫画的热爱。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="py-14 md:py-20 px-4 md:px-8 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                读者口碑
              </h2>
              <p className="mt-3 text-sm text-foreground-500">
                来自<strong>韩漫网站</strong>读者的真实评价
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  name: '小林',
                  role: '大学生',
                  text: '韩漫网站的阅读体验真的太棒了！下拉式浏览非常流畅，不用一直点下一页。我每天都要上来看更新的恋爱漫画，推荐给所有喜欢韩漫的朋友！',
                },
                {
                  name: '阿杰',
                  role: '程序员',
                  text: '作为漫画老读者，我用过很多漫画网站，韩漫网站是体验最好的之一。分类清晰、更新及时，热门漫画大全真的很全，五星推荐！',
                },
                {
                  name: '小婷',
                  role: '设计师',
                  text: '我特别喜欢韩漫网站的画质和排版。每部漫画的详情页设计都很用心，相关推荐也经常能帮我发现新的好作品。每天刷刷韩漫已经成为我的习惯了。',
                },
                {
                  name: '老王',
                  role: '自由职业者',
                  text: '免费阅读而且没有乱七八糟的广告弹窗，这点真的很难得。漫画种类也很全，从热血动作到温馨日常都有，找到了好多好看的韩国漫画。',
                },
                {
                  name: '雪儿',
                  role: '研究生',
                  text: '韩漫网站的悬疑漫画专区是我的最爱！每次更新都第一时间来看，有时候熬夜刷到停不下来。强烈推荐给喜欢悬疑、推理的朋友！',
                },
                {
                  name: '大雄',
                  role: '上班族',
                  text: '下班后的放松方式就是打开韩漫网站看看最新更新的漫画。手机上看也很方便，通勤路上也能追更，真的离不开这个漫画韩漫网址了。',
                },
              ].map((review, i) => (
                <div key={i} className="p-6 rounded-xl bg-background-50 border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{review.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-sm">
                      {review.name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 px-4 md:px-8 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                联系我们
              </h2>
              <p className="mt-3 text-sm text-foreground-500">
                如果您有任何问题或建议，欢迎通过以下方式与我们联系
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-4 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">电子邮箱</h4>
                    <p className="text-sm text-foreground-500 mt-1">contact@hanman.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-accent-500 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">微信公众号</h4>
                    <p className="text-sm text-foreground-500 mt-1">韩漫阅读</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-smartphone-line text-secondary-500 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">客服热线</h4>
                    <p className="text-sm text-foreground-500 mt-1">400-888-9999</p>
                    <p className="text-xs text-foreground-400 mt-1">工作时间：周一至周日 9:00-22:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">公司地址</h4>
                    <p className="text-sm text-foreground-500 mt-1">中国上海市浦东新区张江高科技园区</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/60">
                <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-5">发送消息</h3>

                {formStatus === 'success' ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-4">
                      <i className="ri-check-line text-3xl text-accent-500"></i>
                    </div>
                    <h4 className="text-lg font-semibold text-foreground-800">消息已发送！</h4>
                    <p className="mt-2 text-sm text-foreground-500">感谢您的留言，我们会尽快回复您。</p>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} data-readdy-form>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">姓名</label>
                        <input
                          type="text"
                          name="name"
                          value={formName}
                          onChange={(e) => setFormName(e.target.value)}
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          name="email"
                          value={formEmail}
                          onChange={(e) => setFormEmail(e.target.value)}
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">留言</label>
                        <textarea
                          name="message"
                          value={formMessage}
                          onChange={(e) => setFormMessage(e.target.value)}
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all resize-none"
                          placeholder="请输入您的留言（最多500字）"
                        ></textarea>
                        <div className="mt-1 text-xs text-foreground-400 text-right">
                          {formMessage.length}/500
                        </div>
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden', pointerEvents: 'none' }}
                      />
                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-sm font-semibold rounded-lg transition-colors cursor-pointer"
                      >
                        {formStatus === 'loading' ? '发送中...' : '发送消息'}
                      </button>
                    </div>

                    {formStatus === 'error' && (
                      <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                        <i className="ri-error-warning-line"></i>
                        <span>{formError}</span>
                      </div>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}